import React, { useContext, useMemo, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { HEADER } from 'config';
import Footer from 'layouts/Footer';

import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SessionContext } from '../../auth';
import paths from '../../routes/paths';
import { DashboardType } from '../../types/global';
import { IProfileOrganization } from '../../types/users';
import { findProfileDomain, shouldShowDashboardMaintenance } from '../../utils/domainMaintenance';
import { useQueryPersistentState } from '../../utils/QueryParamsContext';

import { HeaderContext } from './header/context';
import GlobalNav, { NAV_RAIL_WIDTH } from './navigation/GlobalNav';
import { useNavVariant } from './navigation/useNavVariant';
import MainHeader from './header';

export interface MainLayoutProps {
  isDashboard?: boolean;
}

export default function MainLayout({ isDashboard }: MainLayoutProps) {
  const { session, profile } = useContext(SessionContext);
  const theme = useTheme();
  const navVariant = useNavVariant();
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const initialOrganization = profile?.organizations.find((x) => !x.master) as IProfileOrganization;
  const [domainId, setDomainId] = useQueryPersistentState('domain-id', initialOrganization?.domains[0].id);
  const [organizationId, setOrganizationId] = useQueryPersistentState('organization-id', initialOrganization?.id);
  const domain = profile ? findProfileDomain(profile, domainId, organizationId) : undefined;
  const [dashboardType, setDashboardType] = useState(
      domain?.grossRevViewOnly ? DashboardType.Gross : DashboardType.Incremental,
  );
  const isDashboardInMaintenance = profile
    ? shouldShowDashboardMaintenance(profile, domainId, organizationId, isDashboard)
    : false;
  const contextValue = useMemo(() => ({
    domainId,
    setDomainId,
    organizationId,
    setOrganizationId,
    dashboardType,
    setDashboardType,
    isDashboardInMaintenance,
  }), [
    domainId,
    setDomainId,
    organizationId,
    setOrganizationId,
    dashboardType,
    setDashboardType,
    isDashboardInMaintenance,
  ]);

  if (!session) {
    const { pathname, search } = window.location;
    window.sessionStorage.setItem(
      'after-login-path',
      `${pathname}${search}`,
    );
    return (
      <Navigate to={paths.auth.login} />
    );
  }

  if (!profile || profile.organizations.length === 0) {
    return (
      <Navigate to={paths.auth.accessPending} />
    );
  }

  if (profile.status === 'confirmation_pending') {
    return <Navigate to={paths.auth.setPassword} replace />;
  }

  if (!domain) {
    setDomainId(initialOrganization?.domains[0].id);
    setOrganizationId(initialOrganization?.id);
  }

  const desktopMainSx = useMemo(() => ({
    pt: `${HEADER.MAIN_DESKTOP_HEIGHT}px`,
  }), []);
  const desktopHybridLeft = useMemo(() => {
    if (navVariant !== 'hybrid') return null;
    // The permanent rail overlays content, so we pad the main area to keep
    // dashboard visuals from touching the nav.
    return NAV_RAIL_WIDTH + 16;
  }, [navVariant]);

  const mainContent = (() => {
    if (isDashboardInMaintenance) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 320,
          }}
        >
          <Typography variant="h5" color="primary.dark">
            Dashboard in maintenance mode
          </Typography>
        </Box>
      );
    }
    if (navVariant === 'hybrid') {
      return (
        <Box
          sx={{
            [theme.breakpoints.up('lg')]: {
              transform: 'scale(0.96)',
              transformOrigin: 'top left',
              width: '104.1667%',
            },
            height: '100%',
          }}
        >
          <Outlet />
        </Box>
      );
    }
    return <Outlet />;
  })();

  return (
    <HeaderContext.Provider value={contextValue}>
      <Box sx={{ height: 'var(--main-layout-height, 100vh)' }}>
        <Stack
          sx={{
            height: '100%',
            backgroundColor: theme.palette.secondary.lighter,
            ...(isDashboard ? {
              maxWidth: '100vw',
              overflowX: 'hidden',
            } : {}),
          }}
        >
          <MainHeader
            isDashboard={isDashboard}
            onNavOpen={navVariant !== 'off' ? () => setNavDrawerOpen(true) : undefined}
          />

          {navVariant !== 'off' && (
            <GlobalNav
              variant={navVariant}
              headerOffsetPx={HEADER.MAIN_DESKTOP_HEIGHT}
              drawerOpen={navDrawerOpen}
              onDrawerOpenChange={setNavDrawerOpen}
            />
          )}

          <Box
            component="main"
            sx={{
              pt: {
                xs: `${HEADER.MOBILE_HEIGHT}px`,
                md: desktopMainSx.pt,
              },
              ...(desktopHybridLeft ? {
                [theme.breakpoints.up('lg')]: {
                  pl: `${desktopHybridLeft}px`,
                },
              } : {}),
              flexGrow: 1,
            }}
            data-testid="main"
          >
            <Container maxWidth={false} sx={{ height: '100%' }}>
              {mainContent}
            </Container>
          </Box>
          <Footer />
        </Stack>
      </Box>
    </HeaderContext.Provider>
  );
}
